import { Autocomplete, type Item } from '@ads/components-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRevisionDisciplineDropdown } from '@/features/exam-revision/hooks/useRevisionDisciplineDropdown';
import { getAutocompleteSearchMessage } from '@/shared/utils/getAutocompleteSearchMessage';

export interface DisciplineAutocompleteProps {
  /** ID da disciplina selecionada; `''` quando nenhuma. */
  readonly value: string;
  /** Notifica o novo ID de disciplina selecionado (`''` ao limpar). */
  readonly onChange: (disciplineId: string) => void;
  readonly label: string;
  readonly placeholder: string;
}

/**
 * Autocomplete de disciplinas com busca server-side (debounce + mínimo de 3
 * caracteres), no mesmo padrão dos autocompletes da tela de pessoas.
 *
 * Mantém internamente o termo pesquisado e o rótulo selecionado; para fora expõe
 * apenas `value` (ID) e `onChange`, mantendo a tela como dona da seleção.
 */
export function DisciplineAutocomplete({
  value,
  onChange,
  label,
  placeholder,
}: DisciplineAutocompleteProps) {
  const { t: tCommon } = useTranslation('common');

  const [search, setSearch] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');

  const { disciplines, isQuerySuccess } = useRevisionDisciplineDropdown(search);

  const items = disciplines.map((d): Item<string> => ({ label: d.name, value: d.id }));
  const selectedItem: Item<string> = value
    ? { label: selectedLabel, value }
    : { label: '', value: '' };

  const notFoundMessage = getAutocompleteSearchMessage(
    search.length,
    isQuerySuccess,
    items.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  return (
    <Autocomplete<string>
      label={label}
      placeholder={placeholder}
      size="md"
      list={items}
      custom={{ input: '!w-full' }}
      selectedItem={selectedItem}
      {...(notFoundMessage !== undefined ? { notFoundMessage } : {})}
      onChange={e => setSearch(e.target.value)}
      handleChange={item => {
        onChange(item.value ?? '');
        setSelectedLabel(item.label ?? '');
        setSearch('');
      }}
      handleClearQuery={() => {
        onChange('');
        setSelectedLabel('');
        setSearch('');
      }}
    />
  );
}
